import { lazy, Suspense, useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useActivity, useActivities } from "../hooks/useActivities";
import { useActivitiesStore } from "../stores/activitiesStore";
import type { ActivityHRZone } from "@runlens/shared";
import { InsightsPanel } from "../components/insights/InsightsPanel";
import { ChatPanel } from "../components/chat";

// Lazy load charts to reduce initial bundle size (recharts is ~450KB)
const ActivityChartsSection = lazy(() =>
  import("../components/charts/ActivityCharts").then((m) => ({
    default: m.ActivityChartsSection,
  }))
);

function formatDistance(meters: number): string {
  const km = meters / 1000;
  return `${km.toFixed(2)} km`;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatPace(secondsPerKm: number | undefined): string {
  if (!secondsPerKm) return "-";
  const mins = Math.floor(secondsPerKm / 60);
  const secs = Math.floor(secondsPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, "0")} /km`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatTrainingEffect(value: number | undefined | null): string {
  if (value === undefined || value === null) return "-";
  return value.toFixed(1);
}

function getTrainingEffectLabel(value: number | undefined | null): string {
  if (value === undefined || value === null) return "";
  if (value < 1.0) return "None";
  if (value < 2.0) return "Minor";
  if (value < 3.0) return "Maintaining";
  if (value < 4.0) return "Improving";
  if (value < 5.0) return "Highly Improving";
  return "Overreaching";
}

// Stat icons
function DistanceIcon() {
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function DurationIcon() {
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PaceIcon() {
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function TrainingEffectIcon() {
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
}

function StatCard({ icon, label, value, subValue }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-semibold text-gray-900">{value}</div>
      {subValue && <div className="text-sm text-gray-500">{subValue}</div>}
    </div>
  );
}

function getTrainingEffectColor(value: number | undefined | null): string {
  if (value === undefined || value === null) return "bg-gray-200";
  if (value < 1.0) return "bg-gray-300";
  if (value < 2.0) return "bg-blue-300";
  if (value < 3.0) return "bg-green-400";
  if (value < 4.0) return "bg-yellow-400";
  if (value < 5.0) return "bg-orange-400";
  return "bg-red-500";
}

function TrainingEffectItem({ label, value }: { label: string; value: number | undefined | null }) {
  const displayValue = formatTrainingEffect(value);
  const description = getTrainingEffectLabel(value);
  const color = getTrainingEffectColor(value);
  const percentage = value != null ? Math.min((value / 5) * 100, 100) : 0;

  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-2xl font-semibold text-gray-900">{displayValue}</span>
        {description && (
          <span className="text-sm text-gray-500">{description}</span>
        )}
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

const ZONE_COLORS = [
  "bg-gray-300", // Zone 1 - Recovery
  "bg-blue-400", // Zone 2 - Aerobic
  "bg-green-400", // Zone 3 - Tempo
  "bg-yellow-400", // Zone 4 - Threshold
  "bg-red-400", // Zone 5 - Anaerobic
];

function HRZonesChart({ zones, compact = false }: { zones: ActivityHRZone[]; compact?: boolean }) {
  const totalTime = zones.reduce((sum, z) => sum + z.timeInZone, 0);
  if (totalTime === 0) return <span className="text-gray-400 text-sm">No HR data</span>;

  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      {zones.map((zone, index) => {
        const percentage = (zone.timeInZone / totalTime) * 100;
        const minutes = Math.floor(zone.timeInZone / 60);
        const seconds = Math.floor(zone.timeInZone % 60);
        return (
          <div key={zone.zone} className="flex items-center gap-2 text-xs">
            <span className={`${compact ? "w-8" : "w-20"} text-gray-600`}>
              {compact ? `Z${zone.zone}` : `Zone ${zone.zone}`}
            </span>
            <div className={`flex-1 ${compact ? "h-3" : "h-5"} bg-gray-100 rounded-full overflow-hidden`}>
              <div
                className={`h-full ${ZONE_COLORS[index] || "bg-gray-300"} transition-all`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="w-12 text-right text-gray-600">
              {minutes}:{seconds.toString().padStart(2, "0")}
            </span>
            <span className="w-10 text-right text-gray-400">
              {percentage.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse">
      <div className="h-4 w-24 bg-gray-200 rounded mb-4"></div>
      <div className="h-8 w-64 bg-gray-200 rounded mb-2"></div>
      <div className="h-4 w-48 bg-gray-200 rounded mb-6"></div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
            <div className="h-6 w-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

function EditableTitle({
  value,
  onSave,
}: {
  value: string;
  onSave: (newValue: string) => Promise<boolean>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (trimmed === value || trimmed.length === 0) {
      setEditValue(value);
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onSave(trimmed);
    setIsSaving(false);

    if (success) {
      setIsEditing(false);
    } else {
      setEditValue(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className="text-2xl font-bold text-gray-900 bg-white border border-brand-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none w-full max-w-md"
          maxLength={200}
        />
        {isSaving && (
          <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="group flex items-center gap-2 text-left"
      title="Click to edit title"
    >
      <h1 className="text-2xl font-bold text-gray-900">{value}</h1>
      <span className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
        <PencilIcon />
      </span>
    </button>
  );
}

function NotFoundState() {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Activity not found</h3>
      <p className="text-gray-500 mb-4">
        This activity doesn't exist or hasn't been synced yet.
      </p>
      <Link
        to="/app/activities"
        className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
      >
        Back to Activities
      </Link>
    </div>
  );
}

export function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { activity, loading, error, previousActivity, nextActivity } = useActivity(id);
  const { activities, loading: listLoading } = useActivities({ autoFetch: true });
  const updateActivity = useActivitiesStore((s) => s.updateActivity);

  const handleTitleSave = async (newName: string) => {
    if (!id) return false;
    return updateActivity(id, { name: newName });
  };

  const isLoading = loading || (!activity && !error && id);
  const showNavigation = activities.length > 1 || (listLoading && activities.length === 0);

  if (isLoading && !activity) {
    return (
      <div>
        <Link
          to="/app/activities"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <BackIcon />
          Back to Activities
        </Link>
        <LoadingState />
      </div>
    );
  }

  if (!activity || error) {
    return (
      <div>
        <Link
          to="/app/activities"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <BackIcon />
          Back to Activities
        </Link>
        <NotFoundState />
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Back link */}
      <Link
        to="/app/activities"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <BackIcon />
        Back to Activities
      </Link>

      {/* Header with navigation */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <EditableTitle value={activity.name} onSave={handleTitleSave} />
            <p className="text-gray-500 mt-1">{formatDate(activity.startedAt)}</p>
          </div>

          {/* Activity navigation */}
          {showNavigation && (
            <div className="flex items-center gap-1">
              {previousActivity ? (
                <Link
                  to={`/app/activities/${previousActivity.id}`}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  title={`Previous: ${previousActivity.name}`}
                >
                  <ChevronLeftIcon />
                </Link>
              ) : (
                <span className="p-2 rounded-lg border border-gray-100 text-gray-300 cursor-not-allowed">
                  <ChevronLeftIcon />
                </span>
              )}
              {nextActivity ? (
                <Link
                  to={`/app/activities/${nextActivity.id}`}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  title={`Next: ${nextActivity.name}`}
                >
                  <ChevronRightIcon />
                </Link>
              ) : (
                <span className="p-2 rounded-lg border border-gray-100 text-gray-300 cursor-not-allowed">
                  <ChevronRightIcon />
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={<DistanceIcon />}
          label="Distance"
          value={formatDistance(activity.distance)}
        />
        <StatCard
          icon={<DurationIcon />}
          label="Duration"
          value={formatDuration(activity.duration)}
        />
        <StatCard
          icon={<PaceIcon />}
          label="Avg Pace"
          value={formatPace(activity.averagePace)}
        />
        <StatCard
          icon={<HeartIcon />}
          label="Avg Heart Rate"
          value={activity.averageHR ? `${activity.averageHR} bpm` : "-"}
          subValue={activity.maxHR ? `Max: ${activity.maxHR} bpm` : undefined}
        />
        {/* Training Effect */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
            <TrainingEffectIcon />
            <span>Training Effect</span>
          </div>
          <div className="flex gap-6">
            <TrainingEffectItem
              label="Aerobic"
              value={activity.aerobicTrainingEffect}
            />
            <TrainingEffectItem
              label="Anaerobic"
              value={activity.anaerobicTrainingEffect}
            />
          </div>
        </div>

        {/* HR Zones */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <HeartIcon />
            <span>Heart Rate Zones</span>
            {activity.hrZonesEstimated && (
              <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                Estimated from max HR
              </span>
            )}
          </div>
          {activity.hrZones && activity.hrZones.length > 0 ? (
            <HRZonesChart zones={activity.hrZones} compact />
          ) : (
            <p className="text-gray-400 text-sm">No HR zone data available</p>
          )}
        </div>
      </div>

      {/* Activity Charts - lazy loaded */}
      {activity.samples && activity.samples.length > 0 && (
        <Suspense
          fallback={
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Activity Charts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4 h-64 animate-pulse">
                  <div className="h-4 w-24 bg-gray-200 rounded mb-4" />
                  <div className="h-48 bg-gray-100 rounded" />
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4 h-64 animate-pulse">
                  <div className="h-4 w-24 bg-gray-200 rounded mb-4" />
                  <div className="h-48 bg-gray-100 rounded" />
                </div>
              </div>
            </div>
          }
        >
          <ActivityChartsSection samples={activity.samples} hrZones={activity.hrZones} />
        </Suspense>
      )}

      {/* AI Insights and Chat */}
      {id && (
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="w-full md:w-3/5">
            <InsightsPanel
              key={id}
              activityId={id}
              provider={activity.source}
              insightStatus={activity.insightStatus}
            />
          </div>
          <div className="w-full md:w-2/5 md:sticky md:top-4">
            <ChatPanel
              activityId={id}
              provider={activity.source}
            />
          </div>
        </div>
      )}
    </div>
  );
}
